<template lang="pug">
	.creative-template-fields.flex-col.space-medium.flex-1
		.flex-row.space-medium.align-center
			.flex-row.space.align-center
				.font-small.upper.grey {{$tCaption('Template fields')}}
				DxButton(icon="plus", stylingMode="text", :hint="$tCaption('Add', '@actions')", @click="addTemplateField", :disabled="readonly")
		.template-field.flex-row.space(v-for="(templateField, templateFieldIndex) in editingCreative.JsonConfig.templateFields")
			.flex-col.flex-1
				.font-tiny.upper.grey {{$tCaption('Caption')}}
				DxTextBox(:value.sync="templateField.description", :disabled="readonly", valueChangeEvent="input", @value-changed="onTemplateFieldDescriptionChanged(templateField)")
					dx-validator
						dx-required-rule(:message="$tCaption('Description is required')")
						dx-custom-rule(:validation-callback="() => !editingCreative.JsonConfig.templateFields.find(f => (f.description === templateField.description || f.id === templateField.id) && f !== templateField)",
							:message="$tCaption('There is already same template field')")
			.flex-col.space
				.flex-col
					.font-small.upper.grey {{$tCaption('Data type')}}
					DxSelectBox(:dataSource="$templateFieldDataTypes", displayExpr="name", valueExpr="id", :value="templateField.dataType || (templateField.parentServiceOid ? 'service' : 'string')",
						:width="240", @selection-changed="e => onTemplateFieldDataTypeChange(templateField, e)"
					)
				DxCheckBox(v-if="templateField.dataType === 'string'", :text="$tCaption('Memo')", :value="!!templateField.isMemo", @value-changed="e => $set(templateField, 'isMemo', e.value)")
				template(v-if="templateField.dataType === 'service' || templateField.parentServiceOid")
					.flex-col.field
						.font-small.upper.grey {{$tCaption('Data source')}}
						Dropdown(:getTotal="(r) => r.TotalNumberOfRecords", :getItems="(r) => r.Results", 
							:getData="(page,limit,opts) => $Data.getExternalServices(page, limit, {...(opts || {}), baseServices: true })",
							:columns="[{ dataField: 'Name', caption: $tCaption('Service') }]",
							displayExpr="Name",
							valueExpr="Oid",
							:placeholder="$tCaption('Base service')",
							:selectedItems="templateField.parentServiceOid",
							:showColumnHeaders="false",
							:filtersVisible="false",
							:searchVisible="true",
							:multiple="false",
							@select-data="v => $set(templateField, 'parentServiceOid', v && v.Oid || null)")
					.flex-col(v-if="templateField.parentServiceOid")
						Dropdown(:getTotal="(r) => r.Results[0].JsonDefinition.Outputs.length", :getItems="(r) => r.Results[0].JsonDefinition.Outputs.filter(f => !f.Status)",
							:watchReload="templateField.parentServiceOid",
							:getData="(page,limit,opts) => $Data.getExternalServices(page, limit, { byKey: templateField.parentServiceOid })",
							:columns="[{ dataField: 'Name', caption: $tCaption('Output') }]",
							displayExpr="Name",
							valueExpr="Name",
							:placeholder="$tCaption('Output')",
							:selectedItems="templateField.parentServiceOutput",
							:showColumnHeaders="false",
							:filtersVisible="false",
							:searchVisible="true",
							:multiple="false",
							@select-data="v => $set(templateField, 'parentServiceOutput', v && v.Name || null)")
			.flex-col.flex-2(:class="{ 'disabled-controls': readonly }")
				.font-tiny.upper.grey {{$tCaption('Default value')}}									
				BaseParameterSelector(v-bind="getBaseParamBindOptions(templateField)", @change="(paramValues, paramCaptions) => onTemplateFieldParametersChange(templateField, paramValues, paramCaptions)", :hideCaption="true")
			.flex-col.flex-1(:class="[newTemplateFields.includes(templateField) ? 'field' : '']")
				.space.grey.font-tiny.upper {{$tCaption('Id')}}
				.flex-row.align-center(v-if="newTemplateFields.includes(templateField)")
					.grey [!
					.flex-1
						DxTextBox(:value.sync="templateField.id", valueChangeEvent="input")
					.grey ]
				.grey(v-else) {{`[!${templateField.id}]`}}
			DxButton(icon="trash", stylingMode="text", type="danger", :disabled="readonly", :hint="$tCaption('Add', '@actions')",
				@click="removeTemplateField(templateFieldIndex)")
</template>

<script>
	import DxButton from 'devextreme-vue/button';
	import BaseParameterSelector from 'view/webServiceCaller/BaseParameterSelector.vue';3
	import Dropdown from 'app-core/Dropdown.vue';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxValidationGroup from 'devextreme-vue/validation-group';
	import DxValidator, { DxRequiredRule, DxCustomRule } from 'devextreme-vue/validator';
	import serviceHelper from 'view/webServiceCaller/service-helper';

	export default {
		translationCategory: 'creative.Detail',
		name: 'creative-template-fields',
		components: {
			BaseParameterSelector, DxButton, Dropdown, DxTextBox, DxSelectBox,
			DxValidationGroup, DxValidator, DxRequiredRule, DxCustomRule,
		},
		props: ['editingCreative', 'readonly'],
		data() {
			this.$templateFieldDataTypes = [...this.$Data.externalServicesParametersDataTypes(), 'service', ...Object.keys(this.$Data.complexExternalServicesTypes)]
				.map(f => ({ id: f, name: this.$Data.complexExternalServicesTypes[f]?.name || this.$Data.capitalize(f) }));
			return {
				newTemplateFields: []
			};
		},
		computed: {
			
		},
		mounted() {
			if (__DEBUG__)
				window.creativeTemplateFields = this;
		},
		destroyed() {
			delete this.$templateFieldDataTypes;
		},
		methods: {
			addTemplateField() {
				const description = this.$tCaption('New template field') + ` ${this.editingCreative.JsonConfig.templateFields.length + 1}`;
				const templateField = {
					id: this.formatAsKey(description),
					description,
					default: this.$tCaption('Default value')
				};
				this.editingCreative.JsonConfig.templateFields.push(templateField);
				this.newTemplateFields.push(templateField);
			},
			onTemplateFieldDescriptionChanged(templateField) {
				if (__DEBUG__)
					console.debug('onTemplateFieldDescriptionChanged');
				if (this.newTemplateFields.includes(templateField)) {
					templateField.id = this.formatAsKey(templateField.description);
				}
			},
			onTemplateFieldDataTypeChange(templateField, e) {
				if (e.selectedItem?.id && templateField.dataType !== e.selectedItem?.id) {
					if (__DEBUG__)
						console.debug('onTemplateFieldDataTypeChange', templateField, e.selectedItem.id);
					this.$set(templateField, 'dataType', e.selectedItem.id);
					this.$set(templateField, 'isComplex', !!this.$Data.complexExternalServicesTypes[e.selectedItem.id]);
					templateField.default = null;
					delete templateField.services;
					delete templateField.parentServiceOid;
					delete templateField.parentServiceOutput;
				}
			},
			onTemplateFieldParametersChange(templateField, paramValues, paramCaptions) {
				if (__DEBUG__)
					console.debug('onTemplateFieldParametersChange', { templateField, paramValues, paramCaptions });

				if (!templateField.services) {
					templateField.services = [];
				}

				templateField.services = Object.keys(paramValues).map(paramName => {
					const res = {
						Name: paramName,
						Value: paramValues[paramName]
					}
					if (paramCaptions && paramCaptions[paramName]) {
						res.Caption = paramCaptions[paramName];
					}
					return res;
				});

				this.$set(templateField, 'default', paramCaptions && paramCaptions[templateField.id] || paramValues[templateField.id]);
			},
			getBaseParamBindOptions(templateField) {
				return {
					paramResolver: () => serviceHelper.getParamFromTemplateField(templateField, this),
					parameter: null,
					...this.getParamValuesFromTemplateField(templateField)
				}
			},
			getParamValuesFromTemplateField(templateField) {
				return (templateField.services || []).reduce((res, f) => {
					res.paramValues[f.Name] = f.Value || templateField.default;
					if (f.Caption)
						res.paramCaptions[f.Name] = f.Caption;
					return res;
				}, { paramValues: {}, paramCaptions: {} });
			},
			removeTemplateField(templateFieldIndex) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to delete the current template field?'),
					handler: () => {
						this.editingCreative.JsonConfig.templateFields.splice(templateFieldIndex, 1);
					}
				});
				
			},
			formatAsKey(val) {
				return this.$Data.formatStringAsKey(val);
			},
		}
	};
</script>
<style lang='scss'>
	.creative-template-fields {
		
	}
</style>
